import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import DocumentNumberSeriesForm from '../../components/Configuration/DocumentNumberSeriesForm';
import { getDocumentTypeLabel } from '../../utils/documentTypes';
import {
  deleteDocumentNumberSeries,
  getDocumentNumberSeries,
  syncDocumentNumberSeriesFromDocuments,
} from '../../services/documentNumberSeriesService';

const DocumentNumberSeries = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await getDocumentNumberSeries();
      if (response.success) {
        setRows(response.data || []);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar las series',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadRows();
    }
  }, []);

  const filteredRows = rows.filter((row) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      row.series?.toLowerCase().includes(q)
      || row.description?.toLowerCase().includes(q)
    );
  });

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const response = await deleteDocumentNumberSeries(itemToDelete.id);
      if (response.success) {
        setRows((prev) => prev.filter((row) => row.id !== itemToDelete.id));
        setToast({ isOpen: true, message: 'Serie eliminada exitosamente', type: 'success' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la serie',
        type: 'error',
      });
    } finally {
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleSync = async (row) => {
    try {
      const response = await syncDocumentNumberSeriesFromDocuments(row.id);
      if (response.success) {
        setRows((prev) => prev.map((item) => (item.id === row.id ? response.data : item)));
        setToast({
          isOpen: true,
          message: `Siguiente número actualizado a ${response.data.next_number}`,
          type: 'success',
        });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al recalcular la serie',
        type: 'error',
      });
    }
  };

  const previewNumber = (row) => {
    const padding = Math.max(1, parseInt(row.padding_length, 10) || 1);
    const value = parseInt(row.next_number, 10) || 1;
    if (padding <= 1) return String(value);
    return String(value).padStart(padding, '0');
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Series autonumeradas</h1>
              <p className="text-gray-600 mt-1">
                Configure series que asignan automáticamente el siguiente número al crear documentos.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setEditingItem(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium"
            >
              + Nueva serie
            </button>
          </div>

          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Series autonumeradas</span>
          </nav>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-sm text-blue-800">
            Al crear un documento, si ingresa una serie configurada aquí, el sistema completará
            automáticamente el siguiente número correlativo.
          </div>

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por serie o descripción..."
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo doc.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Siguiente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vista previa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">Cargando...</td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay series configuradas</td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold text-gray-900">{row.series}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.document_type ? getDocumentTypeLabel(row.document_type) : 'Todos'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.next_number}</td>
                    <td className="px-4 py-3 text-sm font-mono text-teal-700">{previewNumber(row)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {row.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.description || '—'}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingItem(row);
                          setIsFormOpen(true);
                        }}
                        className="text-teal-600 hover:text-teal-800"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSync(row)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Recalcular
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setItemToDelete(row);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DocumentNumberSeriesForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        onSuccess={(saved) => {
          if (editingItem) {
            setRows((prev) => prev.map((row) => (row.id === saved.id ? saved : row)));
            setToast({ isOpen: true, message: 'Serie actualizada exitosamente', type: 'success' });
          } else {
            setRows((prev) => [...prev, saved]);
            setToast({ isOpen: true, message: 'Serie creada exitosamente', type: 'success' });
          }
        }}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar serie autonumerada"
        message={`¿Eliminar la serie "${itemToDelete?.series}"? Esta acción no afecta documentos ya registrados.`}
        confirmText="Eliminar"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default DocumentNumberSeries;
